import {
  Building2,
  ChevronRight,
  FileText,
  LineChart,
  Phone,
  ReceiptText,
  Settings,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import {
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { PAGE_PERMISSION_KEYS } from "@/lib/data/static/permissionOptions";
import { cn } from "@/lib/utils/cn";
import { Link } from "@tanstack/react-router";

const FIRM_REPORTS_PATH = "/reports/user-productivity";
const FIRM_SETTINGS_PATHS = [
  "/settings/users",
  "/settings/communication",
] as const;

const firmSubNavLabelClass =
  "[&>span:last-child]:min-w-0 [&>span:last-child]:whitespace-nowrap [&>span:last-child]:overflow-hidden [&>span:last-child]:text-ellipsis";

const isFirmSettingsPath = (path: string) =>
  FIRM_SETTINGS_PATHS.some(
    (settingsPath) =>
      path === settingsPath || path.startsWith(`${settingsPath}/`),
  );

export const FirmSubMenu = ({ currentPath }: { currentPath: string }) => {
  const { setOpenMobile } = useSidebar();
  const { currentUser, hasPagePermission } = useUser();
  const [reportsOpen, setReportsOpen] = useState(() =>
    currentPath.startsWith(FIRM_REPORTS_PATH),
  );
  const [settingsOpen, setSettingsOpen] = useState(() =>
    isFirmSettingsPath(currentPath),
  );
  const prevPathRef = useRef(currentPath);

  useEffect(() => {
    const prev = prevPathRef.current;
    prevPathRef.current = currentPath;

    if (
      !prev.startsWith(FIRM_REPORTS_PATH) &&
      currentPath.startsWith(FIRM_REPORTS_PATH)
    ) {
      setReportsOpen(true);
    }

    if (!isFirmSettingsPath(prev) && isFirmSettingsPath(currentPath)) {
      setSettingsOpen(true);
    }
  }, [currentPath]);

  const canViewMyFirm = hasPagePermission(PAGE_PERMISSION_KEYS.firmMyFirm);
  const canViewFirmFiles = hasPagePermission(PAGE_PERMISSION_KEYS.firmFiles);
  const canViewTemplates = hasPagePermission(
    PAGE_PERMISSION_KEYS.firmTemplates,
  );
  const canViewUserProductivityReport = hasPagePermission(
    PAGE_PERMISSION_KEYS.firmReportsUserActivity,
  );
  const canViewUsers = hasPagePermission(
    PAGE_PERMISSION_KEYS.firmSettingsUsers,
  );
  const canViewFirmPhones = hasPagePermission(
    PAGE_PERMISSION_KEYS.firmSettingsPhoneNumbers,
  );
  const canViewCharges = Boolean(currentUser.owner);

  return (
    <SidebarMenuSub className="mt-1.5 mr-0 ml-2 gap-1 border-l-2 border-l-neutral-900 py-0.5 pr-0 pl-3">
      {canViewMyFirm ? (
        <SidebarMenuSubItem>
          <SidebarMenuSubButton asChild className={firmSubNavLabelClass}>
            <Link
              to="/settings/firm"
              className={cn(
                currentPath === "/settings/firm" &&
                  "bg-neutral-900 text-white hover:bg-neutral-700 active:bg-neutral-700",
              )}
              onClick={() => {
                setOpenMobile(false);
              }}
            >
              <Building2 className="mr-2 h-4 w-4 shrink-0" />
              My firm
            </Link>
          </SidebarMenuSubButton>
        </SidebarMenuSubItem>
      ) : null}
      {canViewFirmFiles ? (
        <SidebarMenuSubItem>
          <SidebarMenuSubButton asChild className={firmSubNavLabelClass}>
            <Link
              to="/settings/files"
              className={cn(
                currentPath === "/settings/files" &&
                  "bg-neutral-900 text-white hover:bg-neutral-700 active:bg-neutral-700",
              )}
              onClick={() => {
                setOpenMobile(false);
              }}
            >
              <FileText className="mr-2 h-4 w-4 shrink-0" />
              Files
            </Link>
          </SidebarMenuSubButton>
        </SidebarMenuSubItem>
      ) : null}
      {canViewCharges ? (
        <SidebarMenuSubItem>
          <SidebarMenuSubButton asChild className={firmSubNavLabelClass}>
            <Link
              to="/charges"
              className={cn(
                currentPath === "/charges" &&
                  "bg-neutral-900 text-white hover:bg-neutral-700 active:bg-neutral-700",
              )}
              onClick={() => {
                setOpenMobile(false);
              }}
            >
              <ReceiptText className="mr-2 h-4 w-4 shrink-0" />
              Charges
            </Link>
          </SidebarMenuSubButton>
        </SidebarMenuSubItem>
      ) : null}
      {canViewTemplates ? (
        <SidebarMenuSubItem>
          <SidebarMenuSubButton asChild className={firmSubNavLabelClass}>
            <Link
              to="/settings/templates"
              className={cn(
                currentPath === "/settings/templates" &&
                  "bg-neutral-900 text-white hover:bg-neutral-700 active:bg-neutral-700",
              )}
              onClick={() => {
                setOpenMobile(false);
              }}
            >
              <FileText className="mr-2 h-4 w-4 shrink-0" />
              Templates
            </Link>
          </SidebarMenuSubButton>
        </SidebarMenuSubItem>
      ) : null}
      {canViewUserProductivityReport ? (
        <SidebarMenuSubItem className="list-none p-0">
          <SidebarMenuSubButton
            className={firmSubNavLabelClass}
            onClick={() => {
              setReportsOpen((open) => !open);
            }}
          >
            <LineChart className="mr-2 h-4 w-4 shrink-0" />
            <span className="flex-1 truncate text-left">Reports</span>
            <ChevronRight
              className={cn(
                "h-4 w-4 shrink-0 transition-transform",
                reportsOpen && "rotate-90",
              )}
              aria-hidden
            />
          </SidebarMenuSubButton>
          {reportsOpen ? (
            <SidebarMenuSub className="mt-0.5 mr-0 ml-0 border-l-2 border-l-neutral-300 py-0.5 pr-0 pl-3">
              <SidebarMenuSubItem>
                <SidebarMenuSubButton asChild className={firmSubNavLabelClass}>
                  <Link
                    to="/reports/user-productivity"
                    className={cn(
                      currentPath === "/reports/user-productivity" &&
                        "bg-neutral-900 text-white hover:bg-neutral-700 active:bg-neutral-700",
                    )}
                    onClick={() => {
                      setOpenMobile(false);
                    }}
                  >
                    <LineChart className="mr-2 h-4 w-4 shrink-0" />
                    User activity
                  </Link>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            </SidebarMenuSub>
          ) : null}
        </SidebarMenuSubItem>
      ) : null}
      {canViewUsers || canViewFirmPhones ? (
        <SidebarMenuSubItem className="list-none p-0">
          <SidebarMenuSubButton
            className={firmSubNavLabelClass}
            onClick={() => {
              setSettingsOpen((open) => !open);
            }}
          >
            <Settings className="mr-2 h-4 w-4 shrink-0" />
            <span className="flex-1 truncate text-left">Settings</span>
            <ChevronRight
              className={cn(
                "h-4 w-4 shrink-0 transition-transform",
                settingsOpen && "rotate-90",
              )}
              aria-hidden
            />
          </SidebarMenuSubButton>
          {settingsOpen ? (
            <SidebarMenuSub className="mt-0.5 mr-0 ml-0 border-l-2 border-l-neutral-300 py-0.5 pr-0 pl-3">
              {canViewUsers ? (
                <SidebarMenuSubItem>
                  <SidebarMenuSubButton
                    asChild
                    className={firmSubNavLabelClass}
                  >
                    <Link
                      to="/settings/users"
                      className={cn(
                        currentPath === "/settings/users" &&
                          "bg-neutral-900 text-white hover:bg-neutral-700 active:bg-neutral-700",
                      )}
                      onClick={() => {
                        setOpenMobile(false);
                      }}
                    >
                      <Settings className="mr-2 h-4 w-4 shrink-0" />
                      <span className="min-w-0 truncate">Users</span>
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              ) : null}
              {canViewFirmPhones ? (
                <SidebarMenuSubItem>
                  <SidebarMenuSubButton
                    asChild
                    className={firmSubNavLabelClass}
                  >
                    <Link
                      to="/settings/communication"
                      className={cn(
                        currentPath === "/settings/communication" &&
                          "bg-neutral-900 text-white hover:bg-neutral-700 active:bg-neutral-700",
                      )}
                      onClick={() => {
                        setOpenMobile(false);
                      }}
                    >
                      <Phone className="mr-2 h-4 w-4 shrink-0" />
                      <span className="min-w-0 truncate">Phone numbers</span>
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              ) : null}
            </SidebarMenuSub>
          ) : null}
        </SidebarMenuSubItem>
      ) : null}
    </SidebarMenuSub>
  );
};
